import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useIsMobile } from "../../hooks/responsive";
import BottomSheet from "./BottomSheet";

const maxWidthMap = {
  xs: "20rem",
  sm: "24rem",
  md: "28rem",
  lg: "32rem",
  xl: "36rem",
  "2xl": "42rem",
  "3xl": "48rem",
  "4xl": "56rem",
  "5xl": "64rem",
  "6xl": "72rem",
  "7xl": "80rem",
  full: "100%",
  min: "min-content",
  max: "max-content",
  fit: "fit-content",
  prose: "65ch",
  "500px": "500px",
  "600px": "600px",
};

const Modal = ({
  children,
  onClose,
  title,
  headerContent,
  footerContent,
  maxWidth = "2xl",
  showCloseButton = true,
  position = "center",
  zIndex = 2000,
  customRef,
  mobileFullscreen = false,
  preventOutsideClick = false,
  headerPadding = "tw-p-6",
  bodyPadding = "tw-px-6 tw-py-4",
  open,
  snapPoints,
  forceDesktopView = false,
  maxHeight = "90vh",
  expandOnContentDrag,
  panelClassName = "",
}) => {
  const internalRef = useRef(null);
  const ref = customRef || internalRef;
  const isMobile = useIsMobile();
  const actualMaxWidth = maxWidthMap[maxWidth] || maxWidth;

  useEffect(() => {
    if ((!isMobile || forceDesktopView) && open) {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target) && !preventOutsideClick) {
          onClose();
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [onClose, ref, preventOutsideClick, isMobile, forceDesktopView, open]);

  if (!open) return null;

  if (isMobile && !forceDesktopView) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        title={title}
        headerContent={headerContent}
        footerContent={footerContent}
        showCloseButton={showCloseButton}
        zIndex={zIndex}
        customRef={ref}
        headerPadding={headerPadding === "tw-p-6" ? "" : headerPadding}
        bodyPadding={bodyPadding}
        snapPoints={snapPoints}
        {...(expandOnContentDrag !== undefined ? { expandOnContentDrag } : {})}
      >
        {children}
      </BottomSheet>
    );
  }

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4"
      style={{ zIndex }}
    >
      <div
        className="tw-fixed tw-inset-0 tw-bg-black/50"
        onClick={preventOutsideClick ? undefined : onClose}
        style={{ backdropFilter: "blur(5px)", zIndex }}
      />
      <div
        ref={ref}
        className={`tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-bg-white tw-shadow-2xl ${panelClassName}`}
        style={{
          zIndex: zIndex + 1,
          maxWidth: actualMaxWidth,
          ...(maxHeight === "auto" ? {} : { height: maxHeight }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || headerContent || showCloseButton) && (
          <div
            className={`${headerPadding} tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-gray-100`}
          >
            <div className="tw-w-full">
              {title && (
                <h2 className="tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-2xl tw-font-bold tw-text-transparent">
                  {title}
                </h2>
              )}
              {headerContent}
            </div>
            {showCloseButton && (
              <button onClick={onClose} className="tw-rounded-full tw-p-2 hover:tw-bg-gray-100">
                <X className="tw-h-6 tw-w-6 tw-text-gray-500" />
              </button>
            )}
          </div>
        )}

        <div
          className={`${bodyPadding} tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent ${maxHeight === "auto" ? "" : "tw-flex-1"} tw-overflow-y-auto`}
        >
          {children}
        </div>

        {footerContent && (
          <div className="tw-flex-shrink-0 tw-border-t tw-border-gray-100 tw-p-6">
            {footerContent}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
